import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useAllExpenseSheets, useApproveExpenseSheet, useRejectExpenseSheet, useDeleteExpenseSheet, useBulkApproveExpenseSheets, useBulkRejectExpenseSheets, useBulkDeleteExpenseSheets } from '../../hooks/useExpenses';
import { StatusBadge } from '../../components/workflow/StatusBadge';
import { Spinner } from '../../components/common/Spinner';
import { ConfirmModal } from '../../components/common/Modal';
import { RejectModal } from '../../components/workflow';
import { useAuth } from '../../services/authContext';
import type { ExpenseSheet } from '../../services/api/expenses';
import { useTopScrollbarSync } from '../../hooks/useTopScrollbarSync';
import { formatDisplayDate } from '../../utils/format';

function formatDate(dateString: string | null): string {
  return formatDisplayDate(dateString);
}

function getUserName(user: ExpenseSheet['user']): string {
  if (!user) return 'Unknown';
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  return user.email;
}

export function PendingExpensesPage() {
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [adminView, setAdminView] = useState(false);
  const { data: expenses, isLoading, error } = useAllExpenseSheets(adminView);
  const approveMutation = useApproveExpenseSheet();
  const rejectMutation = useRejectExpenseSheet();
  const deleteMutation = useDeleteExpenseSheet();
  const bulkApproveMutation = useBulkApproveExpenseSheets();
  const bulkRejectMutation = useBulkRejectExpenseSheets();
  const bulkDeleteMutation = useBulkDeleteExpenseSheets();

  // Single item modals
  const [selectedExpense, setSelectedExpense] = useState<ExpenseSheet | null>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showBulkApproveModal, setShowBulkApproveModal] = useState(false);
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  // Filters and pagination from URL params
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || 'submitted');
  const [nameFilter, setNameFilter] = useState(searchParams.get('search') || '');
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = 50;

  // Persist filters in URL (use ref to read current page without depending on searchParams)
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter && statusFilter !== 'submitted') params.set('status', statusFilter);
    if (nameFilter) params.set('search', nameFilter);
    // Don't include page in this effect - it's managed separately
    const existingPage = searchParamsRef.current.get('page');
    if (existingPage && existingPage !== '1') params.set('page', existingPage);
    setSearchParams(params, { replace: true });
  }, [statusFilter, nameFilter, setSearchParams]);

  const setPage = (page: number) => {
    const params = new URLSearchParams(searchParams);
    if (page > 1) {
      params.set('page', String(page));
    } else {
      params.delete('page');
    }
    setSearchParams(params, { replace: true });
    setSelectedIds(new Set());
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    if (currentPage > 1) {
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, nameFilter]);

  // Filter expenses
  const filteredExpenses = useMemo(() => {
    let result = [...(expenses || [])];

    if (statusFilter !== 'all') {
      result = result.filter((e) => e.state === statusFilter);
    }

    if (nameFilter.trim()) {
      const search = nameFilter.toLowerCase();
      result = result.filter((e) => {
        const fullName = `${e.user?.firstName || ''} ${e.user?.lastName || ''}`.toLowerCase();
        const email = (e.user?.email || '').toLowerCase();
        return fullName.includes(search) || email.includes(search);
      });
    }

    result.sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime());

    return result;
  }, [expenses, statusFilter, nameFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredExpenses.length / pageSize);
  const paginatedExpenses = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredExpenses.slice(start, start + pageSize);
  }, [filteredExpenses, currentPage, pageSize]);

  // F21-006: scroll refs + overflow tracking extracted to useTopScrollbarSync
  const { topScrollRef, bottomScrollRef, topScrollContentRef, hasOverflow } =
    useTopScrollbarSync([filteredExpenses]);

  const selectedItems = useMemo(() => {
    return filteredExpenses.filter((e) => selectedIds.has(e.id));
  }, [filteredExpenses, selectedIds]);

  const approvableItems = useMemo(() => {
    return selectedItems.filter((e) => e.state === 'submitted');
  }, [selectedItems]);

  const deletableItems = selectedItems;

  // Selection handlers - work with current page only
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(paginatedExpenses.map((e) => e.id)));
    } else {
      setSelectedIds(new Set());
    }
  }, [paginatedExpenses]);

  const handleSelectOne = useCallback((id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  // Single item handlers
  const handleApproveClick = (expense: ExpenseSheet) => {
    setSelectedExpense(expense);
    setShowApproveModal(true);
  };

  const handleRejectClick = (expense: ExpenseSheet) => {
    setSelectedExpense(expense);
    setShowRejectModal(true);
  };

  const handleDeleteClick = (expense: ExpenseSheet) => {
    setSelectedExpense(expense);
    setShowDeleteModal(true);
  };

  const handleApprove = () => {
    if (selectedExpense) {
      approveMutation.mutate(selectedExpense.id, {
        onSuccess: () => {
          setShowApproveModal(false);
          setSelectedExpense(null);
        },
      });
    }
  };

  const handleReject = (reason: string) => {
    if (selectedExpense) {
      rejectMutation.mutate({ id: selectedExpense.id, reason }, {
        onSuccess: () => {
          setShowRejectModal(false);
          setSelectedExpense(null);
        },
      });
    }
  };

  const handleDelete = () => {
    if (selectedExpense) {
      deleteMutation.mutate(selectedExpense.id, {
        onSuccess: () => {
          setShowDeleteModal(false);
          setSelectedExpense(null);
        },
      });
    }
  };

  // Bulk action handlers
  const handleBulkApprove = () => {
    const ids = approvableItems.map((item) => item.id);
    bulkApproveMutation.mutate(ids, {
      onSuccess: () => {
        setSelectedIds(new Set());
        setShowBulkApproveModal(false);
      },
    });
  };

  const handleBulkReject = (reason: string) => {
    const ids = approvableItems.map((item) => item.id);
    bulkRejectMutation.mutate({ ids, reason }, {
      onSuccess: () => {
        setSelectedIds(new Set());
        setShowBulkRejectModal(false);
      },
    });
  };

  const handleBulkDelete = () => {
    const ids = deletableItems.map((item) => item.id);
    bulkDeleteMutation.mutate(ids, {
      onSuccess: () => {
        setSelectedIds(new Set());
        setShowBulkDeleteModal(false);
      },
    });
  };

  const pageNumbers = Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
    if (totalPages <= 7) return i + 1;
    if (currentPage <= 4) return i + 1;
    if (currentPage >= totalPages - 3) return totalPages - 6 + i;
    return currentPage - 3 + i;
  });

  const PaginationNav = () => (
    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
      <button
        onClick={() => setPage(currentPage - 1)}
        disabled={currentPage <= 1}
        className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="sr-only">Previous</span>
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
        </svg>
      </button>
      {pageNumbers.map((pageNum, i) => (
        <button
          key={`page-${i}`}
          onClick={() => setPage(pageNum)}
          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
            pageNum === currentPage
              ? 'z-10 bg-primary-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
              : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
          }`}
        >
          {pageNum}
        </button>
      ))}
      <button
        onClick={() => setPage(currentPage + 1)}
        disabled={currentPage >= totalPages}
        className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="sr-only">Next</span>
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
        </svg>
      </button>
    </nav>
  );

  const PaginationSummary = () => (
    <p className="text-sm text-gray-700">
      Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
      <span className="font-medium">{Math.min(currentPage * pageSize, filteredExpenses.length)}</span> of{' '}
      <span className="font-medium">{filteredExpenses.length}</span> results
    </p>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load expenses: {(error as Error).message}</p>
      </div>
    );
  }

  const allSelected = paginatedExpenses.length > 0 && paginatedExpenses.every((e) => selectedIds.has(e.id));
  const someSelected = selectedIds.size > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Expenses</h1>
        <p className="mt-1 text-sm text-gray-500">
          View and manage expenses on your projects
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name..."
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">All statuses</option>
          <option value="open">Open</option>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>

        {isAdmin && (
          <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={adminView}
              onChange={(e) => setAdminView(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Show all projects
          </label>
        )}

        {/* Bulk actions */}
        {someSelected && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-gray-600">{selectedIds.size} selected</span>
            {approvableItems.length > 0 && (
              <button
                onClick={() => setShowBulkApproveModal(true)}
                className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
              >
                Approve ({approvableItems.length})
              </button>
            )}
            {approvableItems.length > 0 && (
              <button
                onClick={() => setShowBulkRejectModal(true)}
                className="px-3 py-1.5 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700"
              >
                Reject ({approvableItems.length})
              </button>
            )}
            {deletableItems.length > 0 && (
              <button
                onClick={() => setShowBulkDeleteModal(true)}
                className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Delete ({deletableItems.length})
              </button>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {filteredExpenses.length === 0 ? (
          <div className="text-center py-12">
            <CheckIcon className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No expenses</h3>
            <p className="mt-1 text-sm text-gray-500">
              No expense sheets found.
            </p>
          </div>
        ) : (
          <>
            <style>{`
              .expenses-table-scroll {
                overflow-x: auto;
              }
              .expenses-table-scroll::-webkit-scrollbar {
                height: 20px;
                background: #F3F4F6;
              }
              .expenses-table-scroll::-webkit-scrollbar-track {
                background: #F3F4F6;
                border-top: 1px solid #E5E7EB;
              }
              .expenses-table-scroll::-webkit-scrollbar-thumb {
                background: #9CA3AF;
                border-radius: 10px;
                border: 4px solid #F3F4F6;
              }
              .expenses-table-scroll::-webkit-scrollbar-thumb:hover {
                background: #6B7280;
              }
              .expenses-top-scroll {
                overflow-x: scroll !important;
                overflow-y: hidden;
                width: 100%;
                display: block;
              }
              .expenses-top-scroll::-webkit-scrollbar {
                height: 20px;
                background: #F3F4F6;
              }
              .expenses-top-scroll::-webkit-scrollbar-track {
                background: #F3F4F6;
              }
              .expenses-top-scroll::-webkit-scrollbar-thumb {
                background: #9CA3AF;
                border-radius: 10px;
                border: 4px solid #F3F4F6;
              }
              .expenses-top-scroll::-webkit-scrollbar-thumb:hover {
                background: #6B7280;
              }
            `}</style>
            {/* Top Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3 sm:px-6">
                <PaginationSummary />
                <PaginationNav />
              </div>
            )}
            {hasOverflow && (
              <div ref={topScrollRef} className="expenses-top-scroll border-b border-gray-200">
                <div ref={topScrollContentRef} style={{ height: '1px' }}></div>
              </div>
            )}
            <div ref={bottomScrollRef} className="expenses-table-scroll">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Project
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Receipts
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedExpenses.map((expense) => (
                  <tr key={expense.id} className={`hover:bg-gray-50 ${selectedIds.has(expense.id) ? 'bg-blue-50' : ''}`}>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(expense.id)}
                        onChange={(e) => handleSelectOne(expense.id, e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {isAdmin && expense.user ? (
                        <Link to={`/admin/users/${expense.user.id}`} className="text-primary-600 hover:text-primary-800">
                          {getUserName(expense.user)}
                        </Link>
                      ) : (
                        getUserName(expense.user)
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <Link to={`/expenses/${expense.id}`} className="text-sm text-primary-600 hover:text-primary-800">
                        {expense.description || 'No description'}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {expense.project ? (
                        <Link to={`/projects/${expense.project.id}`} className="text-primary-600 hover:text-primary-800">
                          {expense.project.name}
                        </Link>
                      ) : (
                        <span className="text-gray-500">No project</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {expense.receiptCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {expense.totalAmount.toFixed(2)} CZK
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(expense.updatedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge state={expense.state} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/expenses/${expense.id}`}
                          className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                        >
                          View
                        </Link>
                        {expense.state === 'submitted' && (
                          <>
                            <button
                              onClick={() => handleApproveClick(expense)}
                              className="text-green-600 hover:text-green-700 text-sm font-medium"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleRejectClick(expense)}
                              className="text-orange-600 hover:text-orange-700 text-sm font-medium"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDeleteClick(expense)}
                          className="text-red-600 hover:text-red-700 text-sm font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => setPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <PaginationSummary />
                  </div>
                  <div>
                    <PaginationNav />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Single Approve Modal */}
      <ConfirmModal
        isOpen={showApproveModal}
        onClose={() => {
          setShowApproveModal(false);
          setSelectedExpense(null);
        }}
        onConfirm={handleApprove}
        title="Approve Expense Sheet"
        message={`Are you sure you want to approve ${getUserName(selectedExpense?.user ?? null)}'s expense sheet for ${selectedExpense?.totalAmount.toFixed(2)} CZK?`}
        confirmText="Approve"
        variant="primary"
        isLoading={approveMutation.isPending}
      />

      {/* Single Reject Modal */}
      <RejectModal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setSelectedExpense(null);
        }}
        onConfirm={handleReject}
        isLoading={rejectMutation.isPending}
        employeeName={getUserName(selectedExpense?.user ?? null)}
      />

      {/* Bulk Approve Modal */}
      <ConfirmModal
        isOpen={showBulkApproveModal}
        onClose={() => setShowBulkApproveModal(false)}
        onConfirm={handleBulkApprove}
        title="Bulk Approve Expense Sheets"
        message={`Approve ${approvableItems.length} expense sheet(s)?`}
        confirmText="Approve All"
        variant="primary"
        isLoading={bulkApproveMutation.isPending}
      />

      {/* Bulk Reject Modal */}
      <RejectModal
        isOpen={showBulkRejectModal}
        onClose={() => setShowBulkRejectModal(false)}
        onConfirm={handleBulkReject}
        isLoading={bulkRejectMutation.isPending}
        employeeName={`${approvableItems.length} expense sheet(s)`}
      />

      {/* Delete Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedExpense(null);
        }}
        onConfirm={handleDelete}
        title="Delete Expense Sheet"
        message={`Are you sure you want to delete ${getUserName(selectedExpense?.user ?? null)}'s expense sheet?`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Bulk Delete Modal */}
      <ConfirmModal
        isOpen={showBulkDeleteModal}
        onClose={() => setShowBulkDeleteModal(false)}
        onConfirm={handleBulkDelete}
        title="Bulk Delete Expense Sheets"
        message={`Delete ${deletableItems.length} expense sheet(s)? This action cannot be undone.`}
        confirmText="Delete All"
        variant="danger"
        isLoading={bulkDeleteMutation.isPending}
      />
    </div>
  );
}
